import { useCallback, useEffect, useState } from 'react';
import { Alert, Fade, Grid, Stack } from '@mui/material';
import { ArrowUpward } from '@mui/icons-material';
import { useAtom, useSetAtom } from 'jotai';
import { GenericModal } from '@/components/shared/GenericModal';
import { abyssColors } from '@/theme/colors';
import {
  ActionButton,
  FooterText,
  ImageFrame,
  LineBreak,
  PlaceholderText,
  SectionTitle,
  SelectedOverlay,
  SelectionDialogActions,
  SelectionDialogContent,
  Subtitle,
  UploadCTA,
  crossIconStyles,
  headerStyles,
  modalStyles,
  paperStyles,
} from './styles';
import { CheckIcon } from '@/assets/icons/CheckIcon';
import { MappedReportPointOfInterests } from '@/types';
import {
  File,
  ReportDetailsDocument,
  ReportDetailsQuery,
  useUpdatePoiImagesMutation,
} from '@/__generated__/graphql';
import * as state from '@/components/Analysis/state';
import { getCloudfrontUrl } from '@/utils/cloudfront';
import { ImageIcon } from '@/assets/icons/ImageIcon';
import { dialogOverlay } from '@/components/UserManagement/styles';
import { Image } from '@/components/Analysis/shared/Image';

type ReportDocument = ReportDetailsQuery['report'];

type ModalProps = {
  open: boolean;
  handleClose: () => void;
  disableUpload?: boolean;
  poiDetails: MappedReportPointOfInterests;
  allImagesOfSelectedPOI: File[];
};

const showError = (message: string) => {
  return (
    <div>
      <Alert severity="error">{message}</Alert>
    </div>
  );
};

const showSuccess = (message: string) => {
  return <Alert severity="success">{message}</Alert>;
};

export const UploadSection = () => {
  return (
    <>
      <Stack gap={2}>
        <Stack direction="row" alignItems="center" gap={1}>
          <SectionTitle>Upload from your device</SectionTitle>
          <Subtitle>(max 4 uploads)</Subtitle>
        </Stack>
        <UploadCTA>
          <Stack alignItems="center">
            <ArrowUpward sx={{ fontSize: '1.6rem', color: abyssColors.primary[300] }} />
            <Subtitle>Upload</Subtitle>
          </Stack>
        </UploadCTA>
      </Stack>
      <LineBreak />
    </>
  );
};

export const ImageSelectionModal = ({
  open,
  handleClose,
  disableUpload = false,
  poiDetails,
  allImagesOfSelectedPOI,
}: ModalProps) => {
  const setSnackbarMessage = useSetAtom(state.snackbarMessage);
  const [selectedReport, setSelectedReport] = useAtom(state.selectedReport);
  const [selectedImages, setSelectedImages] = useState<(File | null)[]>(
    selectedReport?.selectedPointOfInterestImages?.find(
      (selected) => selected?.pointOfInterestId === poiDetails.id
    )?.selectedImages ?? []
  );

  const initializeSelectedImages = useCallback(() => {
    if (selectedReport) {
      const poiSelectedImages = selectedReport.selectedPointOfInterestImages?.find(
        (selected) => selected?.pointOfInterestId === poiDetails.id
      )?.selectedImages;
      if (poiSelectedImages) {
        setSelectedImages(poiSelectedImages);
      }
    }
  }, [poiDetails.id, selectedReport]);

  useEffect(() => {
    initializeSelectedImages();
  }, [initializeSelectedImages, poiDetails.id, selectedReport]);

  useEffect(() => {
    if (open) {
      initializeSelectedImages();
    } else {
      setSelectedImages([]);
    }
  }, [initializeSelectedImages, open]);

  const [mutateSelectedImages] = useUpdatePoiImagesMutation({
    refetchQueries: [ReportDetailsDocument],
    onCompleted: (response) => {
      if (!response?.updateReportPointOfInterestImages) {
        setSnackbarMessage({
          shouldShow: true,
          content: showError('Something went wrong'),
        });
        return;
      }
      setSnackbarMessage({
        shouldShow: true,
        content: showSuccess('Selected images updated successfully'),
      });
      setSelectedReport(
        (previous) =>
          ({
            ...previous,
            selectedPointOfInterestImages:
              response?.updateReportPointOfInterestImages.selectedPointOfInterestImages,
          } as ReportDocument)
      );
    },
    onError: (errorReponse) => {
      setSnackbarMessage({
        shouldShow: true,
        content: showError(errorReponse.message),
      });
    },
  });

  const handleSelection = (image: File) => {
    const index = selectedImages.findIndex(
      (selected) => selected?.resourcePath === image.resourcePath
    );
    if (index === -1) {
      setSelectedImages([...selectedImages, image]);
    } else {
      setSelectedImages(
        selectedImages.filter((selected) => selected?.resourcePath !== image.resourcePath)
      );
    }
  };

  const handleUpdate = () => {
    const mappedImages = selectedImages.map((selected) => {
      return selected
        ? {
            name: selected.name,
            fileName: selected.fileName,
            type: selected.type,
            resourcePath: selected.resourcePath,
          }
        : selected;
    });

    if (selectedReport?.id) {
      mutateSelectedImages({
        variables: {
          input: {
            reportId: selectedReport?.id,
            pointOfInterestId: poiDetails.id,
            selectedImages: mappedImages,
          },
        },
      });
      handleClose();
    }
  };

  return (
    <GenericModal
      open={open}
      handleClose={handleClose}
      title="Adding more images"
      sx={{ ...modalStyles, ...dialogOverlay }}
      backgroundPaperStyle={paperStyles}
      headerStyleProp={headerStyles}
      crossIconStyle={crossIconStyles}
    >
      <SelectionDialogContent data-testid="image-selection">
        {disableUpload || <UploadSection />}
        <Stack gap={2}>
          <SectionTitle>Select from uploaded images</SectionTitle>
          {allImagesOfSelectedPOI.length > 0 ? (
            <Grid container sx={{ width: '408px' }} spacing={2}>
              {allImagesOfSelectedPOI.map((image) => (
                <Grid item xs={4} key={image.fileName}>
                  <ImageFrame
                    onClick={() => {
                      handleSelection(image);
                    }}
                    data-testid="selection-image"
                  >
                    <Fade
                      data-testid={
                        selectedImages
                          .map((selected) => selected?.resourcePath)
                          .includes(image?.resourcePath)
                          ? 'selected-image'
                          : 'unselected-image'
                      }
                      in={selectedImages
                        .map((selected) => selected?.resourcePath)
                        .includes(image?.resourcePath)}
                    >
                      <SelectedOverlay>
                        <CheckIcon />
                      </SelectedOverlay>
                    </Fade>
                    <Image
                      imgSx={{ width: '100%', height: '100%', objectFit: 'cover' }}
                      sx={{ position: 'initial' }}
                      src={getCloudfrontUrl(image?.resourcePath ?? '')}
                      alt={image.name}
                      fallbackSize="2.4rem"
                      fontSize="1rem"
                    />
                  </ImageFrame>
                </Grid>
              ))}
            </Grid>
          ) : (
            <Stack height={disableUpload ? '332px' : '194px'} justifyContent="center">
              <Stack alignItems="center" gap={1}>
                <ImageIcon />
                <PlaceholderText>No images available for selection</PlaceholderText>
              </Stack>
            </Stack>
          )}
        </Stack>
      </SelectionDialogContent>
      <SelectionDialogActions>
        <FooterText>{selectedImages.length} selected</FooterText>
        <Stack direction="row" gap={2}>
          <ActionButton onClick={handleClose}>
            <span>Cancel</span>
          </ActionButton>
          <ActionButton
            variant="contained"
            contained
            onClick={handleUpdate}
            disabled={selectedImages.length === 0}
          >
            Add
          </ActionButton>
        </Stack>
      </SelectionDialogActions>
    </GenericModal>
  );
};
